import { SessionContextCanceledError as CanceledError } from '@/lib/session-cancellation';
import { useSessionTransition } from '@/stores/sessionTransition';
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { beginSessionRecovery, sessionPaused } from '@/stores/sessionRecovery';
import { sessionSnapshot } from '@/lib/session-snapshot';
import { validateSessionPermit } from '@/lib/session-permit';
import { useAuthStore } from '@/stores/auth';
import { activeTenantSlug } from '@/lib/tenant-context';
import { APP_BUILD } from '@/lib/app-version';
import { isForbiddenError } from '@/lib/isForbiddenError';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Resolve tenant slug from (in priority order):
 * 1. VITE_TENANT_SLUG env var (explicit override)
 * 2. Subdomain from current hostname (e.g., "clinic1.valiosascrm.com.br" → "clinic1")
 * 3. null — let the API resolve tenant from email
 */
function resolveTenantSlug(): string | null {
  const envSlug = import.meta.env.VITE_TENANT_SLUG;
  if (envSlug) return envSlug;

  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);

  if (!isLocalhost && !isIP && parts.length >= 3) {
    const subdomain = parts[0];
    const reserved = ['www', 'api', 'app', 'admin', 'staging', 'dev', 'valiosascrm'];
    if (!reserved.includes(subdomain)) {
      return subdomain;
    }
  }

  return null;
}

const initialTenantSlug = resolveTenantSlug();

export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Request interceptor: attach X-Tenant-ID dynamically (skip auth endpoints — they resolve tenant internally)
/**
 * Resolve by the authenticated tenantId first, then its persisted slug. A host
 * fallback is only used before a tenant identity exists. Never pick clinics[0]
 * as a substitute for the active session.
 *
 * Story FE-Tenant — computeTenantSlug é a forma PURA da cadeia acima, compartilhada com
 * o guard reativo useTenantSlug(): guard e header nunca divergem.
 */
type TenantSlugSource = {
  tenantSlug: string | null;
  user: Parameters<typeof activeTenantSlug>[0];
};

export function computeTenantSlug(state: TenantSlugSource): string | null {
  if (state.user?.tenantId) return activeTenantSlug(state.user);
  return activeTenantSlug(state.user) || state.tenantSlug || initialTenantSlug;
}

export function resolveTenantHeaderSlug(): string | null {
  return computeTenantSlug(useAuthStore.getState());
}

/**
 * Story FE-Tenant — fail-closed no cliente: request tenant-scoped sem slug resolvível é
 * rejeitada SINTETICAMENTE (nunca sai à rede), espelhando o fail-closed do backend
 * (TenantPrismaService). O erro não tem `response`, então o interceptor de 401/refresh
 * não dispara (sem loop de refresh/logout).
 *
 * Allowlist: o prefixo `/auth/` foi VERIFICADO endpoint a endpoint (inventário da story
 * FE-Tenant, 2026-07-29): as rotas públicas (@Public: login/register/onboarding/accept-invite/
 * forgot/reset/refresh) são pré-auth por definição, e as autenticadas (/auth/me, logout,
 * switch-clinic, socket-token, …) resolvem tenant pelo JWT no backend — o header já era
 * omitido para todas e seguem funcionando. Nenhuma OUTRA rota tenant-less usa este client.
 */
export class MissingTenantError extends Error {
  readonly code = 'TENANT_SLUG_MISSING';
  constructor(url?: string) {
    super(
      `Request tenant-scoped bloqueada no cliente: nenhum tenant resolvido para ${url ?? '(url desconhecida)'} (sessão sem tenant — faça login novamente ou conclua o onboarding).`,
    );
    this.name = 'MissingTenantError';
  }
}

const requestOwners = new WeakMap<InternalAxiosRequestConfig, { owner: string; duringSwitch: boolean }>();
const sessionRead = (url?: string) => url === '/auth/me' || url === '/auth/socket-token';
const sessionBound = (url?: string) => sessionRead(url) || [
  '/auth/password', '/auth/create-clinic', '/auth/switch-clinic', '/auth/logout',
  '/auth/impersonation/end', '/auth/membership-invites/accept',
].includes(url ?? '');
const currentOwner = sessionSnapshot;
function obsoleteRequest(config?: InternalAxiosRequestConfig) {
  if (!config || (config.url?.startsWith('/auth/') && !sessionBound(config.url))) return false;
  const snapshot = requestOwners.get(config);
  return (useSessionTransition.getState().isSwitchingTenant && !snapshot?.duringSwitch)
    || (snapshot !== undefined && snapshot.owner !== currentOwner());
}

api.interceptors.request.use((config) => {
  const isAuthEndpoint = config.url?.startsWith('/auth/');
  // RC-063: o servidor recusa login de aba sem build (a de antes do deploy) e pede recarregar.
  if (config.url === '/auth/login') config.params = { ...config.params, build: APP_BUILD };
  if (!isAuthEndpoint || sessionBound(config.url)) {
    requestOwners.set(config, { owner: currentOwner(), duringSwitch: useSessionTransition.getState().isSwitchingTenant });
  }
  const permit = validateSessionPermit(config);
  const confirmsSession = !!permit;
  if (sessionPaused() && !permit) throw new CanceledError('Confirme a clínica para continuar');
  if (permit) config.headers['X-Session-Context'] = permit.context;
  if (sessionRead(config.url) && !confirmsSession && useSessionTransition.getState().isSwitchingTenant) {
    throw new CanceledError('Troca de clínica em andamento');
  }
  if (!isAuthEndpoint || (sessionBound(config.url) && !confirmsSession)) {
    const user = useAuthStore.getState().user;
    if (user?.id && user.tenantId) config.headers['X-Session-Context'] = `${user.id}:${user.tenantId}`;
  }
  if (!isAuthEndpoint) {
    if (useSessionTransition.getState().isSwitchingTenant) throw new CanceledError('Troca de clínica em andamento');
    const slug = resolveTenantHeaderSlug();
    if (!slug) {
      return Promise.reject(new MissingTenantError(config.url));
    }
    config.headers['X-Tenant-ID'] = slug;
  }
  return config;
});

// Response interceptor - handle token refresh via cookies
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/** Prevent a late refresh response from overwriting the new tenant's cookies. */
export function waitForSessionRefresh(): Promise<void> {
  if (!isRefreshing) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    failedQueue.push({ resolve: () => resolve(), reject });
  });
}

function processQueue(error: unknown) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => {
    if (obsoleteRequest(response.config)) throw new CanceledError('Contexto da clínica alterado');
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosError['config'] & { _retry?: boolean };
    if (obsoleteRequest(originalRequest)) throw new CanceledError('Contexto da clínica alterado');

    if (error.response?.status === 409 &&
        (error.response.data as { code?: string })?.code === 'SESSION_CONTEXT_CHANGED') {
      if (!originalRequest?.sessionPermit) beginSessionRecovery(useAuthStore.getState().user);
      return Promise.reject(error);
    }

    // Never intercept 401s from auth endpoints — would cause a deadlock
    // (refresh 401 re-enters the interceptor while isRefreshing=true, freezing forever)
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          if (obsoleteRequest(originalRequest)) throw new CanceledError('Contexto da clínica alterado');
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      const refreshOwner = currentOwner();

      try {
        await api.post('/auth/refresh');
        if (refreshOwner !== currentOwner()) throw new CanceledError('Sessão de acesso alterada.');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        if (refreshOwner === currentOwner()) useAuthStore.getState().expireSession();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Story 37 (auditoria) — extrai a mensagem real da API (string ou array do class-validator)
 * para o toast, com fallback. Antes vários onError descartavam a causa e mostravam um
 * genérico ("Erro ao...") que escondia o motivo real do 400/403.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const msg = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string' && msg.trim()) return msg;
  return fallback;
}

// Story 70.3 (RC-170) — gravação de configuração recusada por permissão: a tela diz o motivo e a quem
// pedir, não só desfaz o interruptor.
export const NO_PERMISSION_TO_CHANGE = 'Você não tem permissão para mudar isso. Peça à administradora.';

/** UX-70.3-004: 403 com motivo próprio (ex.: clínica suspensa, `tenant.middleware.ts`) mostra o motivo da API. */
const PERMISSION_GUARD_PREFIX = 'Acesso negado';

export function settingsWriteErrorMessage(error: unknown, fallback: string): string {
  if (!isForbiddenError(error)) return apiErrorMessage(error, fallback);
  const msg = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  const isPermissionGuard = typeof msg !== 'string' || !msg.trim() || msg.startsWith(PERMISSION_GUARD_PREFIX);
  return isPermissionGuard ? NO_PERMISSION_TO_CHANGE : apiErrorMessage(error, fallback);
}

// Story 65.33 — a gravação passou do tempo no servidor. A transação foi desfeita: nada ficou
// gravado e o mesmo pedido pode ser enviado de novo.
export const TRANSACTION_TIMEOUT_CODE = 'TRANSACTION_TIMEOUT';
export const NOTHING_RECORDED_RETRY = 'Nada foi gravado. A operação demorou mais que o esperado; tente de novo.';

export function isTransactionTimeout(error: unknown): boolean {
  return (error as { response?: { data?: { code?: string } } })?.response?.data?.code === TRANSACTION_TIMEOUT_CODE;
}

/** Mensagem de uma gravação financeira: o tempo esgotado diz que nada foi gravado; o resto é o que a API disse. */
export function financialWriteErrorMessage(error: unknown, fallback: string): string {
  return isTransactionTimeout(error) ? NOTHING_RECORDED_RETRY : apiErrorMessage(error, fallback);
}

export default api;
